import { inject, Injectable } from '@angular/core';
import { selectHasActiveAccountMembership } from '@mkp/account-membership/state';
import { selectActiveAccounts } from '@mkp/account/state';
import { selectWorkspaceFromUrlStatus, WorkspaceFromUrlStatus } from '@mkp/auth/state';
import { layoutActions } from '@mkp/layout/actions';
import {
  selectIsNavigationCollapsed,
  selectLeftBoxContent,
  selectRightBoxContent,
  selectShowNavigation,
  selectTopHeader,
} from '@mkp/layout/state';
import {
  areHeaderItemsEqual,
  getItemsConfig,
  getLoginLinkConfig,
  getLoginLinkPlatformConfig,
  TOP_HEADER_STORE,
  TopHeaderStoreInterface,
} from '@mkp/shared/ui-app-header';
import {
  APP_LANGUAGES,
  Language,
  LanguageIso,
  LanguageMapper,
  TrafficFunnel,
} from '@mkp/shared/models';
import { BreakpointObserverService } from '@mkp/shared/util-device';
import { Store } from '@ngrx/store';
import { selectActivePlatformGroup } from '@mkp/product-options/state';
import { getLogoPlatformClass } from '@mkp/shared/util-platform';
import { appHeaderActions } from '@mkp/core/state/actions';
import { selectInterfaceLanguage, selectUsernamePasswordLoginPreferred } from '@mkp/core/state';
import { selectRouteParam } from '@mkp/shared/util-router';
import {
  selectIsBoUser,
  selectLoggedInUser,
  selectUserDisplayType,
} from '@mkp/user/state-logged-in/selectors';
import { combineLatest, map, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Injectable()
export class TopHeaderStore implements TopHeaderStoreInterface {
  private readonly store = inject(Store);
  private readonly isMobile$ = inject(BreakpointObserverService).isMobile$;
  private readonly isNavCollapsed$ = this.store.select(selectIsNavigationCollapsed);
  private readonly topHeader$ = this.store.select(selectTopHeader);
  private readonly activePlatformGroup$ = this.store.select(selectActivePlatformGroup(''));
  private readonly platformClasses$ = combineLatest({
    funnel: this.store.select(selectRouteParam('funnel')) as Observable<TrafficFunnel>,
    displayType: this.store.select(selectUserDisplayType),
  }).pipe(map(({ funnel, displayType }) => getLogoPlatformClass(funnel, displayType)));

  private readonly languages$ = this.store.select(selectInterfaceLanguage).pipe(
    map((current) => ({
      options: APP_LANGUAGES,
      selected: LanguageMapper.toCode(current ?? LanguageIso.GERMAN),
    }))
  );

  private readonly isActiveUser$ = combineLatest({
    workspaceStatus: this.store.select(selectWorkspaceFromUrlStatus),
    isBoUser: this.store.select(selectIsBoUser),
    activeAccounts: this.store.select(selectActiveAccounts),
  }).pipe(
    map(({ workspaceStatus, isBoUser, activeAccounts }) =>
      workspaceStatus === WorkspaceFromUrlStatus.INIT
        ? true
        : !!isBoUser || activeAccounts.length > 0
    )
  );

  private readonly itemsConfig$ = combineLatest({
    topHeader: this.topHeader$,
    platformGroup: this.activePlatformGroup$,
  }).pipe(
    map(({ topHeader, platformGroup }) => {
      const showPlatformStyle = getLoginLinkConfig(topHeader).platformStyle;
      return showPlatformStyle
        ? getLoginLinkPlatformConfig(topHeader, platformGroup)
        : getItemsConfig(topHeader);
    })
  );

  public readonly leftBoxItems$ = this.store
    .select(selectLeftBoxContent)
    .pipe(distinctUntilChanged(areHeaderItemsEqual));

  public readonly rightBoxItems$ = this.store
    .select(selectRightBoxContent)
    .pipe(distinctUntilChanged(areHeaderItemsEqual));

  public readonly vm$ = combineLatest({
    activePlatformGroup: this.activePlatformGroup$,
    hasAccountMembership: this.store.select(selectHasActiveAccountMembership),
    showNavigation: this.store.select(selectShowNavigation),
    isBoUser: this.store.select(selectIsBoUser),
    isMobile: this.isMobile$,
    isLight: this.topHeader$.pipe(map((topHeader) => topHeader.isLight)),
    isNavCollapsed: this.isNavCollapsed$,
    isActiveUser: this.isActiveUser$,
    languages: this.languages$,
    platformClasses: this.platformClasses$,
    user: this.store.select(selectLoggedInUser),
    itemsConfig: this.itemsConfig$,
    usernamePasswordLoginPreferred: this.store.select(selectUsernamePasswordLoginPreferred),
  });

  public toggleNavCollapsed(): void {
    this.store.dispatch(layoutActions.toggleNavigation());
  }

  public changeLanguage(language: Language) {
    const { iso: languageIso } = language;
    this.store.dispatch(appHeaderActions.switchAppLanguage({ languageIso }));
  }
}

export const provideTopHeaderStore = () => ({
  provide: TOP_HEADER_STORE,
  useClass: TopHeaderStore,
});
